import {
  ALL_FORM_OFFICES,
  FULL_COUNTRY_ACCESS_ROLES,
  OFFICE_TO_ADMISSION_COUNTRY,
  resolveOfficesFromSiteUrl,
} from "@/src/constants/accessControl";
import type { AdmissionOfficeCountry } from "@/src/utils/admissionOffice/resolveAdmissionOfficeRouteFromTask";
import type { FormOfficeApi } from "@/src/utils/tasks/normalizeFormOfficeForApi";

export interface CountryAccessResolution {
  userRole: string;
  hasFullCountryAccess: boolean;
  allowedOffices: FormOfficeApi[];
  allowedAdmissionCountries: AdmissionOfficeCountry[];
}

const isFullCountryAccessRole = (role: string): boolean =>
  (FULL_COUNTRY_ACCESS_ROLES as readonly string[]).includes(role);

const resolveOfficesFromRolePrefix = (role: string): FormOfficeApi[] | null => {
  if (role.startsWith("malta-")) return ["MALTA"];
  if (role.startsWith("ukraine-")) return ["UKRAINE"];
  if (role.startsWith("poland-")) return ["POLAND"];
  return null;
};

/**
 * Point 3 — country access from `user.role`.
 * Admin / Sub Admin / Application HR / Documents HR → all offices.
 * Roles like `ukraine-documents-curator` → only that country.
 */
export const resolveCountryAccessFromRole = (rawRole?: string | null): CountryAccessResolution => {
  const userRole = String(rawRole ?? "").trim().toLowerCase();

  if (!userRole || isFullCountryAccessRole(userRole)) {
    return {
      userRole,
      hasFullCountryAccess: true,
      allowedOffices: ALL_FORM_OFFICES,
      allowedAdmissionCountries: ALL_FORM_OFFICES.map((office) => OFFICE_TO_ADMISSION_COUNTRY[office]),
    };
  }

  const lockedOffices = resolveOfficesFromRolePrefix(userRole);
  if (lockedOffices) {
    return {
      userRole,
      hasFullCountryAccess: false,
      allowedOffices: lockedOffices,
      allowedAdmissionCountries: lockedOffices.map((office) => OFFICE_TO_ADMISSION_COUNTRY[office]),
    };
  }

  // Unknown role — do not lock the product accidentally.
  return {
    userRole,
    hasFullCountryAccess: true,
    allowedOffices: ALL_FORM_OFFICES,
    allowedAdmissionCountries: ALL_FORM_OFFICES.map((office) => OFFICE_TO_ADMISSION_COUNTRY[office]),
  };
};

/**
 * Intersect role country access with the selected `office_url`.
 * Example: `feu.com.pl` → Poland + Malta; `All` does not restrict countries.
 */
export const resolveEffectiveCountryAccess = (
  rawRole?: string | null,
  siteUrl?: string | null,
): CountryAccessResolution => {
  const fromRole = resolveCountryAccessFromRole(rawRole);
  const siteOffices = resolveOfficesFromSiteUrl(siteUrl);
  if (!siteOffices) return fromRole;

  const allowedOffices = fromRole.allowedOffices.filter((office) => siteOffices.includes(office));
  const effectiveOffices = allowedOffices.length ? allowedOffices : fromRole.allowedOffices;

  return {
    userRole: fromRole.userRole,
    hasFullCountryAccess: effectiveOffices.length === ALL_FORM_OFFICES.length,
    allowedOffices: effectiveOffices,
    allowedAdmissionCountries: effectiveOffices.map((office) => OFFICE_TO_ADMISSION_COUNTRY[office]),
  };
};
